import { type MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import throttle from "lodash/throttle";
import Debug from "../../../../../common-deprecated/Debug";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../redux/store";
import { isMaster, isSlave } from "../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { sendJsonToSalesman } from "../../../../../common-deprecated/utils";
import { SALESMAN_3D_EXTERIOR_POSITION } from "../../../../../common-deprecated/constants";
import { ExteriorSpin3DState, setExteriorSpin3D } from "../../../../../common-deprecated/redux/actions/UtilActions";
import { useDispatch } from "../../../../../shared-logic/redux/store";
import { getCarImageFromConfig } from "../../../../shared-logic/utils/typedUtils";

type ExciteParamType = {
    debug: boolean;
    delay: number;
    light: boolean;
    shadow: boolean;
    shadowQuality: boolean;
    mobile: boolean;
    antialias: boolean;
    drive: boolean;
    hdr: string;
    sampleLevel: number;
    camera: {
        x: number;
        y: number;
        z: number;
    };
    wheel: number;
};

type ExciteEventType = {
    file?: string;
    message?: string;
    percent?: number;
};

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface EXCITE {
    // eslint-disable-next-line @typescript-eslint/no-misused-new
    new (canvas: HTMLCanvasElement | null, json: unknown, params: ExciteParamType): EXCITE;
    start: () => void;
    setConfiguration: (JSONObject: unknown) => void;
    setCamera: (time: number, cameraPosition: { x: number; y: number; z: number }, wheelOrientation: number) => void;
    setDriveMode: (mode: boolean) => void;
    setInteractiveModel: (value: boolean) => void;
    getCamera: () => {
        azimuth: number;
        enabled: boolean;
        polar: number;
        wheel: number;
        x: number;
        y: number;
        z: number;
    };
    setObjectAnimation: (name: string, time: number) => void;
    addEventListener: (event: string, listener: (event: ExciteEventType) => void) => void;
    removeEventListener: (event: string, listener: (event: ExciteEventType) => void) => void;
}

type ExciteScriptType = {
    EXCITE: EXCITE;
};

const useExciteJS = (load: boolean): ExciteScriptType | null => {
    const excitePath = useBnbSelector((state) => `${state.commonSettings.resourcePath}/js/excite/tme3D.min.js`);
    const threeJSPath = useBnbSelector((state) => `${state.commonSettings.resourcePath}/js/excite/three.min.js`);
    const [script, setScript] = useState<ExciteScriptType | null>(null);

    useEffect(() => {
        if (load && !script) {
            window.require([threeJSPath], (threeJS) => {
                window.THREE = threeJS;
                window.require([excitePath], () => {
                    // Return the _3D object set on window
                    setScript((window as any)._3D);
                });
            });
        }
    }, [load]);

    return script;
};

const useExteriorSpin3D = (
    canvasRef: MutableRefObject<HTMLCanvasElement | null>,
): {
    loading: boolean;
    loadingPercentage: number;
    showReset: boolean;
    resetCar: () => void;
} => {
    const excite = useExciteJS(true);
    const settings = useBnbSelector((state) => state.commonSettings);
    const car = useBnbSelector((state) => state.car);
    const basket = useBnbSelector((state) => state.basket);
    const salesmanOverride = useBnbSelector((state) => state.utils.salesmanOverrides.current3DExteriorPosition);
    const buildIsMaster = isMaster(settings);
    const buildIsSlave = isSlave(settings);
    const dispatch = useDispatch<BuildAndBuyDispatchType>();

    // Check to see if the 3D engine has been initialized already
    const [exciteEngineIsInitialized, setExciteEngineIsInitialized] = useState<boolean>(false);

    // State to use for the loading/ loading percentage of the Excite engine
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingPercentage, setLoadingPercentage] = useState<number>(0);

    // State to show the determine if the reset Button is shown
    const [showReset, setShowReset] = useState<boolean>(false);

    // State containing the dynamic Json Object used to set configuration of 3D model
    const [exciteJsonObj, setExciteJsonObj] = useState(undefined);

    const renderer = useRef<EXCITE | null>(null);

    /**
     * used for getting the correct CCIS URl when the configuration of thez car changes.
     */
    const jsonConfig = useMemo(() => {
        return getCarImageFromConfig(
            settings,
            "",
            "",
            car.model.id,
            car.id,
            {},
            basket.colour,
            basket.upholstery,
            basket.wheel,
            basket.packs,
            basket.extras,
            false,
            undefined,
            true,
        );
    }, [basket.colour, basket.upholstery, basket.wheel, basket.packs, basket.extras]);

    /**
     * Used for reseting the camera position and the doors + hiding the resetButton
     */
    const resetCar = (): void => {
        if (renderer.current) {
            renderer.current.setCamera(
                3000,
                {
                    x: 3.534049679774671,
                    y: 0.8812411207654867,
                    z: -7.724928212839733,
                },
                0,
            );

            setShowReset(false);
        }
    };

    /**
     * Helper for getting the current Camera position
     */
    const getPosition = (): { x: number; y: number; z: number } | null => {
        if (!renderer.current) return null;
        const { x, y, z } = renderer.current.getCamera();
        return { x, y, z };
    };

    /**
     * used to fetch the JSON from the returned CCis Url when it changes.
     */
    useEffect(() => {
        const fetchExciteConfig = async (): Promise<void> => {
            try {
                const result = await fetch(jsonConfig, {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                });

                const parsedResult = await result.json();
                setExciteJsonObj(parsedResult);
            } catch (e) {
                Debug.error("failed to fetch the 3D JSON Object", e);
                dispatch(setExteriorSpin3D(ExteriorSpin3DState.Error));
            }
        };

        fetchExciteConfig();
    }, [jsonConfig]);

    /**
     * used to initialize the excite 3D engine when the script is loaded.
     */
    useEffect(() => {
        if (excite && !exciteEngineIsInitialized && exciteJsonObj) {
            try {
                // Initial parameters for the engine
                const parameters: ExciteParamType = {
                    debug: false,
                    delay: 30,
                    light: true,
                    shadow: true,
                    shadowQuality: true,
                    mobile: false,
                    drive: false,
                    antialias: true,
                    hdr: "studio-00",
                    sampleLevel: 4,
                    camera: {
                        x: 3.534049679774671,
                        y: 0.8812411207654867,
                        z: -7.724928212839733,
                    },
                    wheel: 0,
                };

                // Initialise 3DEXCITE engine
                renderer.current = new excite.EXCITE(canvasRef.current, exciteJsonObj, parameters);

                if (!renderer.current) {
                    Debug.error("3D Script could not be initialized");
                    dispatch(setExteriorSpin3D(ExteriorSpin3DState.Error));
                    return;
                }

                renderer.current.addEventListener("onLoadingStart", () => {
                    setLoading(true);
                    setLoadingPercentage(0);
                });

                renderer.current.addEventListener("onLoadingProgress", (event) => {
                    setLoadingPercentage(event.percent || 0);
                });

                // 3D model is visible and can be configured
                renderer.current.addEventListener("onPreparationCompleted", () => {
                    setLoading(false);
                });

                renderer.current.addEventListener("onLoadingError", (event) => {
                    Debug.error(`Failed to load file ${event.file as string}`);
                    dispatch(setExteriorSpin3D(ExteriorSpin3DState.Error));
                });

                renderer.current.addEventListener("onError", (event) => {
                    Debug.error(`Error: ${event.message as string}`);
                    dispatch(setExteriorSpin3D(ExteriorSpin3DState.Error));
                });

                renderer.current.addEventListener("onInteractionStart", () => {
                    setShowReset(true);
                });

                // Start the engine after initialisation is done
                renderer.current.start();

                // Set the excite 3D engine as initialized
                setExciteEngineIsInitialized(true);
            } catch (error) {
                Debug.error((error as Error).message);
                dispatch(setExteriorSpin3D(ExteriorSpin3DState.Error));
            }
        }
    }, [excite, exciteJsonObj]);

    /**
     * Used for setting the new configuration when the JSON changes.
     */
    useEffect(() => {
        if (renderer.current && exciteEngineIsInitialized && exciteJsonObj) {
            // Configure the vehicle sending the new JSON
            renderer.current.setConfiguration(exciteJsonObj);
        }
    }, [exciteJsonObj]);

    // Used for removing any interactivity of the doors etc..
    useEffect(() => {
        if (!loading && renderer.current) {
            renderer.current.setInteractiveModel(false);
        }
    }, [loading]);

    // Send angles to billboard
    useEffect(() => {
        if (!renderer.current || !buildIsMaster) return;

        const updateBillboard: () => void = throttle(() => {
            sendJsonToSalesman({
                action: SALESMAN_3D_EXTERIOR_POSITION,
                data: getPosition(),
            });
        }, 300); // needs to be greater than the animationTimeout in use3DSpin so the animation from last time has definitely finished

        renderer.current.addEventListener("onInteractionChange", updateBillboard);
        return () => renderer.current!.removeEventListener("onInteractionChange", updateBillboard);
    }, [renderer.current]);

    // Receive angles from salesman and update controls
    useEffect(() => {
        if (!buildIsSlave || salesmanOverride.x === null || salesmanOverride.y === null || salesmanOverride.z === null)
            return;

        if (renderer.current) {
            renderer.current.setCamera(
                0,
                {
                    x: salesmanOverride.x,
                    y: salesmanOverride.y,
                    z: salesmanOverride.z,
                },
                0,
            );
        }

        // Little hacky way to make sure the resetButton is shown when you pass along the camera instead of interacting
        setShowReset(true);
    }, [salesmanOverride]);

    // Easter egg: enable drive mode when pressing all arrow keys
    const pressedKeys = useRef<string[]>([]);
    useEffect(() => {
        const enableDrive = (event: KeyboardEvent): void => {
            const allArrowsPressed = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].every((key) =>
                pressedKeys.current.includes(key),
            );
            if (allArrowsPressed) {
                if (renderer.current) renderer.current.setDriveMode(true);
            } else {
                pressedKeys.current.push(event.key);
            }
        };

        window.addEventListener("keydown", enableDrive);
        return () => window.removeEventListener("keydown", enableDrive);
    }, []);

    return {
        loading,
        loadingPercentage,
        showReset,
        resetCar,
    };
};

export default useExteriorSpin3D;
